import React, { useState, useEffect, useRef } from 'react';
import { getAutoOpenPagesData } from '../services/api';
import './AutoOpenPagesPresetDropdown.css';
import { matchesSearch } from '../utils/search';

const AutoOpenPagesPresetDropdown = ({ value, onChange, placeholder = "Select preset..." }) => {
  const [presets, setPresets] = useState([]);
  const [filteredPresets, setFilteredPresets] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch presets on component mount
  useEffect(() => {
    const fetchPresets = async () => {
      setLoading(true);
      try {
        const response = await getAutoOpenPagesData();
        const presetsData = response.data || {};
        
        // Convert object to array of {key, value} pairs
        const presetsArray = Object.entries(presetsData).map(([key, value]) => ({
          key,
          value
        }));
        
        setPresets(presetsArray);
        setFilteredPresets(presetsArray);
      } catch (error) {
        console.error('Error fetching auto open pages presets:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPresets();
  }, []);

  // Filter presets based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredPresets(presets);
    } else {
      const filtered = presets.filter(preset => 
        matchesSearch(searchTerm, preset.key, ...(Array.isArray(preset.value) ? preset.value : []))
      );
      setFilteredPresets(filtered);
    }
  }, [searchTerm, presets]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (preset) => {
    onChange(preset.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputClick = () => {
    setIsOpen(true);
  };

  const getDisplayValue = () => {
    if (!value || !Array.isArray(value) || value.length === 0) return '';
    const selectedPreset = presets.find(preset => 
      Array.isArray(preset.value) && 
      preset.value.length === value.length &&
      preset.value.every((item, index) => item === value[index])
    );
    return selectedPreset ? selectedPreset.key : value.join(', ');
  };

  return (
    <div className="auto-open-pages-preset-dropdown" ref={dropdownRef}>
      <input
        type="text"
        value={isOpen ? searchTerm : getDisplayValue()}
        onChange={handleInputChange}
        onClick={handleInputClick}
        placeholder={placeholder}
        className="auto-open-pages-preset-dropdown-input"
        readOnly={!isOpen}
      />
      {isOpen && (
        <div className="auto-open-pages-preset-dropdown-menu">
          {loading ? (
            <div className="dropdown-loading">Loading presets...</div>
          ) : filteredPresets.length === 0 ? (
            <div className="dropdown-no-results">No presets found</div>
          ) : (
            filteredPresets.map((preset) => (
              <div
                key={preset.key}
                className="dropdown-option"
                onClick={() => handleSelect(preset)}
              >
                <div className="preset-key">{preset.key}</div>
                <div className="preset-value">
                  {Array.isArray(preset.value) ? preset.value.join(', ') : preset.value}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AutoOpenPagesPresetDropdown;
